import Vue from 'vue';
import Component from 'vue-class-component';
import { Async } from '@/utils/AsyncDecorators';
import { versioneInSituStore } from '@/modules/VersioneInSitu/store/VersioneInSitu.store';
import { areaSaggioStore } from '@/modules/AreaSaggio/store/areaSaggio.store';
import { areaSaggioClasseDiametricaStore } from '@/modules/AreaSaggioClasseDiametrica/store/areaSaggioClasseDiametrica.store';
import { domainStore } from '@/modules/domains';
import { ClasseDiametricaIntersection } from '../../utils/classeDimatrica';
import { Watch } from 'vue-property-decorator';
import Row from './row.vue'
import { store } from '@/store';
import { printerHelper } from '@/modules/printer';
import { CalculationUtils } from '../../utils/CalculationUtils';
import { valenzaDenrologica } from '../../utils/valenzaDenrologica';
import { reportStore } from '../../store/report.store';

@Component({ components: { Row } })
export default class Index extends Vue {
  @Async({ async get() { return await versioneInSituStore.getters.current() }, default: {}, lazy: false })
  currentVersion: server.VersioneInSitu;

  @Async({ async get() { return (await areaSaggioStore.getters.getAll()).sort((a, b) => a.nome.localeCompare(b.nome)); }, default: [], lazy: false })
  areeSaggio: server.areaSaggio[];

  @Async({ async get() { return await areaSaggioClasseDiametricaStore.getters.getAll() }, default: [], lazy: false })
  areeSaggioClasseDiametrica: server.areaSaggioClasseDiametrica[];

  @Async({ async get() { return await await domainStore.getters.specieArboree(); }, default: [], lazy: false })
  specieArboree: domains.domini[];

  @Watch('filter.text')
  change(n, o) { this.showsize = 20; }
  showsize: number = 20;

  get areeSaggioAutocomplete() {
    if (this.areeSaggio.length <= 0) return [];
    return this.areeSaggio.map((m) => {
      return {
        key: m.id,
        value: m.id,
        description: m.nome,
      } as server.autocomplete
    })
  }

  get areaSaggioId() {
    return store.getters.navigation.selected("areaSaggio");
  }
  get navigation() {
    if (!this.areaSaggioId) return null
    return this.areeSaggioAutocomplete.find(f => f.key === this.areaSaggioId) || {} as server.autocomplete;
  }
  set navigation(value: server.autocomplete) {
    if (value.key !== this.areaSaggioId) {
      store.actions.navigation.set_filter({ area: "areaSaggio", id: value ? value.key : null });
      reportStore.actions.set_logarithmic_regression(value ? value.key : null);
    }
  }

  get classiDiametricheExtended(): IClasseDiametrica[] {
    if (!this.areaSaggioId) return [];
    return this.areeSaggioClasseDiametrica
      .filter(i => i.areaSaggioId === this.areaSaggioId)
      .map(m => new ClasseDiametricaIntersection(m, this.areeSaggio.find(f => f.id === m.areaSaggioId), this.specieArboree.find(f => f.id === m.specie)))
      .sort((a, b) => {
        const specieOrder = a.specieOrderIndex - b.specieOrderIndex;
        if (specieOrder !== 0)
          return specieOrder;
        return a.diametro - b.diametro;
      });
  }

  get classiDiametricheResized(): {
    diametro: number;
    specie: string;
    numeroPiante: number;
    areaInHa: number;
    numeroPianteHa: number;
    altezzaIpsometrica: number;
    volumeUnitario: number;
    areaBasimetricaAdEttaro: number;
    volumeAdEttaro: number;
  }[] {
    return this.dataGrouped.slice(0, this.showsize);
  }

  get classiDiametricheGrouped(): { diametro: number, specieArboree: { specie: string, volume: number }[], totale: number }[] {
    if (this.classiDiametricheExtended.length <= 0) return [];

    var reduced = this.classiDiametricheExtended.reduce((a, c) => {
      const diametro = c.diametro;
      if (!a[diametro]) {
        a[diametro] = { diametro: diametro, specieArboree: [], totale: 0 };
      }

      const specie = this.specieArboree.find(f => f.id === c.specie).value;
      let specieArborea = a[diametro].specieArboree.find(f => f.specie === specie);

      if (!specieArborea) {
        specieArborea = { specie: specie, volume: 0 };
        a[diametro].specieArboree.push(specieArborea);
      }
      specieArborea.volume += c.volumeAdEttaro;
      a[diametro].totale += parseFloat(c.volumeAdEttaro.toFixed(3));

      return a;
    }, []);

    return Object.values(reduced).sort((a, b) => a.diametro - b.diametro);
  }

  get formulaRegression() {
    return reportStore.getters.regression()?.string;
  }

  get dataGrouped(): {
    diametro: number;
    specie: string;
    specieArborea: domains.domini;
    numeroPiante: number;
    areaInHa: number;
    numeroPianteHa: number;
    altezzaIpsometrica: number;
    volumeUnitario: number;
    areaBasimetricaAdEttaro: number;
    volumeAdEttaro: number;
  }[] {
    if (this.classiDiametricheExtended.length <= 0 || this.classiDiametricheGrouped.length <= 0) return [];
    const dataGrouped: any = [];
    const _specie = [...new Set(this.classiDiametricheExtended.map(m => m.specieArborea))];
    const _classiDiametriche = [...new Set(this.classiDiametricheGrouped.map(m => m.diametro))];
    _specie.forEach((specie) => {
      _classiDiametriche.forEach((classeDiametrica) => {
        const values = this.classiDiametricheExtended.filter(f => f.specieArborea.value === specie.value && f.diametro === classeDiametrica) || [];
        const numeroPiante = values.map(m => m.contatore).reduce((a, b) => a + b);
        const areaInHa = values.map(m => m.areaInHa).reduce((a, b) => a + b) / values.length;
        const numeroPianteHa = CalculationUtils.getNumeroPianteAdEttaro(numeroPiante, areaInHa);
        const altezzaIpsometrica = CalculationUtils.getAltezzaIpsometrica(classeDiametrica);
        const areaClasseDiametrica = CalculationUtils.getAreaClasseDiametrica(classeDiametrica);
        const areaBasimetricaAdEttaro = CalculationUtils.getAreaBasimetricaAdEttaro(areaClasseDiametrica, numeroPianteHa);

        const volumeUnitario = valenzaDenrologica.getVolumeUnitario(specie.value, classeDiametrica, altezzaIpsometrica);
        const volumeAdEttaro = CalculationUtils.getVolumeAdEttaro(numeroPianteHa, volumeUnitario);

        const elem = {
          diametro: classeDiametrica,
          specie: specie.value,
          specieArborea: specie,
          numeroPiante: numeroPiante,
          areaInHa: areaInHa,
          numeroPianteHa: numeroPianteHa,
          altezzaIpsometrica: altezzaIpsometrica,
          volumeUnitario: volumeUnitario,
          areaBasimetricaAdEttaro: areaBasimetricaAdEttaro,
          volumeAdEttaro: volumeAdEttaro
        };
        dataGrouped.push(elem);
      })
    });
    return dataGrouped;
  }

  exportExcel() {
    var arrayToExport: any[] = this.dataGrouped.map(m => {
      const obj = {};
      obj["Diametro"] = m.diametro;
      obj["Specie"] = `${m.specieArborea.label} ${m.specie}`;
      obj["Numero piante"] = m.numeroPiante;
      obj["Area in ha"] = m.areaInHa;
      obj["Numero piante ad ettaro"] = m.numeroPianteHa;
      obj[`Altezza calcolata con la formula ipsometrica ${this.formulaRegression}`] = m.altezzaIpsometrica;
      obj["Volume unitario"] = m.volumeUnitario;
      obj["Area basimetrica ad ettaro"] = m.areaBasimetricaAdEttaro;
      obj["Volume ad ettaro"] = m.volumeAdEttaro;

      return obj;
    });
    printerHelper.printExcel("dati-generali", arrayToExport, "Dati Generali");
  }
}